import clsx from 'clsx';
import type { ReactNode } from 'react';

/** A keyboard cap. Every shortcut in the app is named with one of these. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={clsx('animate-spin', className)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A thin determinate bar. Used for scans and exports. */
export function ProgressBar({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div
      className="h-[3px] w-full overflow-hidden rounded-full bg-ink-800"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      <div
        className="h-full rounded-full bg-ink-100 transition-[width] duration-200"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Empty({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-20 text-center">
      {icon && <div className="text-ink-600">{icon}</div>}
      <div className="flex flex-col gap-1.5">
        <h2 className="text-head font-medium text-ink-100">{title}</h2>
        <p className="max-w-sm text-caption text-ink-400">{body}</p>
      </div>
      {action}
    </div>
  );
}

/** A bordered strip of statistics — used on project cards and the export dialog. */
export function StatRow({ items }: { items: { label: string; value: string | number }[] }) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-0.5">
          <dt className="label">{item.label}</dt>
          <dd className="tnum text-caption text-ink-100">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
